import { useEffect, useMemo, useRef, useState } from 'react'
import {
  subscribeToSyncFailures,
  readDismissals,
  dismissFailures,
  activeFailures,
  SYNC_SOURCE_LABELS,
  DISMISS_STORAGE_KEY,
  type SyncFailure,
} from '../services/syncFailures'
import { parseFirestoreDateStr } from '../services/appointments'
import { scrubVendorText } from '../utils/scrubVendorText'
import { humanizeSyncError } from '../utils/humanizeSyncError'
import type { Clinic } from '../types'

interface SyncHealthChipProps {
  orgId: string
  /** The signed-in user's clinic claim (null for admins / unassigned). */
  clinicId: string | null
  isAdmin: boolean
  clinics: Clinic[]
}

/** Past this age a bare weekday is ambiguous over the 7-day window —
 *  "failed Tue 6:00 AM" read on a Tuesday looks like this morning. */
const WEEKDAY_ONLY_MAX_AGE_MS = 5 * 24 * 60 * 60 * 1000

/** "Sat 6:00 AM" recently; "Tue, Jul 7 6:00 AM" once the weekday alone
 *  could be misread as this week (mirrors formatAffectedDay's style). */
function formatWhen(date: Date | null): string {
  if (!date) return 'recently'
  const stale = Date.now() - date.getTime() > WEEKDAY_ONLY_MAX_AGE_MS
  const day = stale
    ? date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : date.toLocaleDateString(undefined, { weekday: 'short' })
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `${day} ${time}`
}

/** The affected schedule day, "Fri, Jul 3" — which day's appointments are
 *  missing (distinct from when the sync failed). */
function formatAffectedDay(appointmentDate: string): string {
  try {
    return parseFirestoreDateStr(appointmentDate)
      .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  } catch {
    return appointmentDate
  }
}

/**
 * Ambient sync-health indicator in the top bar (#128). Renders nothing while
 * every recent sync is healthy; the moment a pull goes terminal it shows a
 * red "N sync(s) failed" chip, live via onSnapshot. Clicking opens a popover
 * with the per-clinic story: which sync, when it failed, attempts, last
 * success, and the error detail (vendor names scrubbed). Auto-clears when a
 * later sync succeeds;
 * dismissal is per failure cycle, so a still-failing sync comes back.
 */
export default function SyncHealthChip({ orgId, clinicId, isAdmin, clinics }: SyncHealthChipProps) {
  const [failures, setFailures] = useState<SyncFailure[]>([])
  const [dismissals, setDismissals] = useState<Record<string, string>>(() => readDismissals())
  // The popover is "open for" a specific failure-set signature. When the set
  // changes — a failure clears on recovery, or a new one lands — the signature
  // no longer matches and the popover closes on its own (derived state, no
  // effect needed).
  const [openFor, setOpenFor] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Non-admins without a clinic claim can't read runs (firestore.rules
    // clinicMatch) — don't open a subscription that would only error.
    if (!isAdmin && !clinicId) return
    return subscribeToSyncFailures(orgId, clinicId, isAdmin, setFailures)
  }, [orgId, clinicId, isAdmin])

  useEffect(() => {
    // A dismissal in another tab fires `storage` here — re-read so both tabs
    // agree (key === null means the whole store was cleared).
    function handleStorage(e: StorageEvent) {
      if (e.key === null || e.key === DISMISS_STORAGE_KEY) {
        setDismissals(readDismissals())
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const visible = useMemo(
    () => activeFailures(failures, dismissals),
    [failures, dismissals],
  )
  const signature = visible.map(f => f.dismissKey).join('|')
  const open = openFor !== null && openFor === signature

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpenFor(null)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenFor(null)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  if (visible.length === 0) return null

  // Admins see every clinic, so a name missing from the list (archived after
  // the failure) must not read "Your clinic" — that's wrong-audience copy.
  const clinicName = (id: string) =>
    clinics.find(c => c.id === id)?.name ?? (isAdmin ? `Clinic ${id.slice(0, 6)}…` : 'Your clinic')

  const handleDismiss = () => {
    // Pass the full failure set so pruning keeps a still-failing sibling's
    // earlier dismissal while dropping runs that left the window.
    setDismissals(dismissFailures(visible, failures))
    setOpenFor(null)
  }

  return (
    <div className="sync-health" ref={rootRef} role="status" aria-live="polite">
      <button
        type="button"
        className="sync-health__chip"
        aria-expanded={open}
        onClick={() => setOpenFor(open ? null : signature)}
      >
        <span className="sync-health__dot" aria-hidden="true" />
        {visible.length === 1 ? '1 sync failed' : `${visible.length} syncs failed`}
      </button>
      {open && (
        <div className="sync-health__popover">
          <div className="sync-health__popover-head">Data sync issues</div>
          {visible.map(f => {
            // Scrubbing can empty the string (e.g. a bare vendor URL) — gate
            // the box on the scrubbed value, not the raw one.
            const errorDetail = scrubVendorText(humanizeSyncError(f.errorDetail))
            return (
              <div key={f.runId} className="sync-health__row">
                <div className="sync-health__row-title">
                  {clinicName(f.clinicId)}
                  <span>failed {formatWhen(f.failedAt)}</span>
                </div>
                <div className="sync-health__row-body">
                  {SYNC_SOURCE_LABELS[f.source]} for <b>{formatAffectedDay(f.appointmentDate)}</b> didn't update.{' '}
                  {f.attempts > 1 ? `${f.attempts} attempts failed` : 'The attempt failed'}
                  {f.lastSuccessAt
                    ? ` · last success ${formatWhen(f.lastSuccessAt)}.`
                    : ' · no successful sync in the last 7 days.'}
                </div>
                {errorDetail && (
                  <div className="sync-health__row-error"><b>Error:</b> {errorDetail}</div>
                )}
              </div>
            )
          })}
          <div className="sync-health__popover-foot">
            <span>Clears automatically when that day's sync succeeds.</span>
            <button type="button" onClick={handleDismiss}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  )
}
